import {
  DEFAULT_CONNECTIONS,
  MOCK_FEED_ITEMS,
  MOCK_MS_RESOURCE_PICKERS,
  MOCK_PRODUCTIVITY,
  MOCK_ALIGNMENT_RESPONSES,
  MOCK_COMMUNICATIONS_ISSUES,
  MOCK_PROJECT_NUMBER,
} from './communicationsMockData.js';
import { normalizeFeedItem } from './communicationsPresenters.js';

const connectionOverrides = new Map();
const feedOverrides = new Map();
const resolvedThreads = new Set();

function cloneConnections(projectId) {
  const override = connectionOverrides.get(projectId);
  if (override) return JSON.parse(JSON.stringify(override));
  return JSON.parse(JSON.stringify(DEFAULT_CONNECTIONS));
}

function cloneFeed(projectId) {
  const override = feedOverrides.get(projectId);
  const base = override || MOCK_FEED_ITEMS;
  return base.map((item) => normalizeFeedItem(item));
}

export async function getProjectConnections(projectId) {
  await delay(80);
  return cloneConnections(projectId);
}

export async function getCommunicationsFeed(projectId, _filters) {
  await delay(120);
  return { items: cloneFeed(projectId), nextToken: null };
}

export async function getProductivitySummary(_projectId) {
  await delay(60);
  return JSON.parse(JSON.stringify(MOCK_PRODUCTIVITY));
}

export async function getCommunicationsIssues(_projectId) {
  await delay(60);
  return JSON.parse(JSON.stringify(MOCK_COMMUNICATIONS_ISSUES));
}

export async function configureCommunications(projectId, config) {
  await delay(200);
  const projectNumber = MOCK_PROJECT_NUMBER;
  const connections = {
    outlook: {
      connected: true,
      name:
        config?.outlook?.mode === 'link'
          ? MOCK_MS_RESOURCE_PICKERS.outlookFolders.find((f) => f.id === config.outlook.selectedExistingId)?.label
          : `Inbox/Projects/${projectNumber} - ${config?.outlook?.name || 'Project'}`,
      url: '#',
      unreadCount: 0,
      latestActivity: 'Folder linked',
      alignmentStatus: 'aligned',
    },
    teams: {
      connected: true,
      name: config?.teams?.name || 'Project Team',
      channel: config?.teams?.channel || '#general',
      url: '#',
      latestActivity: 'Team linked',
      alignmentStatus: 'aligned',
    },
    planner: {
      connected: true,
      name: config?.planner?.name || 'Project Deliverables',
      url: '#',
      openTasks: 0,
      latestActivity: 'Plan linked',
      alignmentStatus: 'aligned',
    },
  };
  connectionOverrides.set(projectId, connections);
  return connections;
}

export async function listAvailableMicrosoftResources(_projectId, resourceType) {
  await delay(50);
  if (resourceType === 'outlook') return MOCK_MS_RESOURCE_PICKERS.outlookFolders;
  if (resourceType === 'teams') return MOCK_MS_RESOURCE_PICKERS.teams;
  if (resourceType === 'planner') return MOCK_MS_RESOURCE_PICKERS.plannerPlans;
  return [];
}

export async function refreshFeed(projectId) {
  return getCommunicationsFeed(projectId);
}

export async function askAlignmentQuestion(_projectId, { threadKey, question }) {
  await delay(400);
  const canned = MOCK_ALIGNMENT_RESPONSES[threadKey];
  if (canned) {
    return {
      question,
      summary: canned.summary,
      suggestedResolution: canned.suggestedResolution,
      sourcesCited: canned.sourcesCited,
    };
  }
  return {
    question,
    summary: 'No alignment analysis available for this topic in preview mode.',
    suggestedResolution: 'Record an aligned decision once the team confirms the governing source.',
    sourcesCited: [],
  };
}

export async function recordAlignedDecision(projectId, { threadKey, decisionText }) {
  await delay(150);
  resolvedThreads.add(threadKey);

  const items = cloneFeed(projectId).map((item) => {
    if (item.threadKey !== threadKey) return item;
    return {
      ...item,
      decisionPosition: decisionText,
      preview: `Aligned decision: ${decisionText}`,
    };
  });
  feedOverrides.set(projectId, items);

  const connections = cloneConnections(projectId);
  connections.outlook.alignmentStatus = 'aligned';
  connections.teams.alignmentStatus = 'aligned';
  connections.planner.alignmentStatus = 'aligned';
  connectionOverrides.set(projectId, connections);

  return { threadKey, decisionText };
}

export function isThreadResolved(threadKey) {
  return resolvedThreads.has(threadKey);
}

function delay(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
